// CW Squad presence engine: tracks genuine interaction, heartbeats every 60s.
// Server sets auto-BRB after 20 min quiet; the next interaction flips back to Active.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../../lib/api";

const Ctx = createContext(null);
export const useSquad = () => useContext(Ctx);
const AWAY_MS = 20 * 60 * 1000;

export function SquadProvider({ children }) {
  const [me, setMe] = useState(null);
  const [squad, setSquad] = useState(null);
  const last = useRef(Date.now());
  const returning = useRef(false);
  const counts = useRef((() => { try { return JSON.parse(localStorage.getItem("squad_pending")) || { clicks: 0, keys: 0 }; } catch { return { clicks: 0, keys: 0 }; } })());
  const meRef = useRef(null);
  const location = useLocation();
  meRef.current = me;

  const inflight = useRef({ clicks: 0, keys: 0 });
  // Pending = not yet confirmed by the server (unsent + in-flight) — survives reloads.
  const persist = useCallback(() => {
    const p = { clicks: counts.current.clicks + inflight.current.clicks, keys: counts.current.keys + inflight.current.keys };
    if (p.clicks || p.keys) localStorage.setItem("squad_pending", JSON.stringify(p)); else localStorage.removeItem("squad_pending");
  }, []);
  const loadSquad = useCallback(() => api.get("/squad").then(({ data }) => setSquad(data)).catch(() => {}), []);
  const beat = useCallback(() => {
    const c = counts.current; counts.current = { clicks: 0, keys: 0 };
    inflight.current = { clicks: inflight.current.clicks + c.clicks, keys: inflight.current.keys + c.keys };
    return api.post("/squad/heartbeat", { idle_ms: Date.now() - last.current, ...c })
    .then(({ data }) => {
      inflight.current = { clicks: inflight.current.clicks - c.clicks, keys: inflight.current.keys - c.keys };
      persist();
      if (meRef.current?.status !== data.status) loadSquad(); setMe(data);
    })
    .catch(() => {
      inflight.current = { clicks: inflight.current.clicks - c.clicks, keys: inflight.current.keys - c.keys };
      counts.current = { clicks: counts.current.clicks + c.clicks, keys: counts.current.keys + c.keys };
      persist();
    });
  }, [loadSquad, persist]);

  useEffect(() => { last.current = Date.now(); }, [location.pathname]);

  useEffect(() => {
    const mark = (e) => {
      last.current = Date.now();
      if (e?.type === "mousedown" || e?.type === "touchstart") counts.current.clicks += 1;
      else if (e?.type === "keydown") counts.current.keys += 1;
      if (e?.type === "mousedown" || e?.type === "touchstart" || e?.type === "keydown") persist();
      const m = meRef.current;
      if (m?.status === "brb" && m.auto && !returning.current) {
        returning.current = true;
        beat().finally(() => { returning.current = false; });
      }
    };
    const evs = ["mousedown", "keydown", "wheel", "touchstart", "mousemove"];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    beat(); loadSquad();
    const onVis = () => { if (document.visibilityState === "hidden" && (counts.current.clicks || counts.current.keys)) beat(); };
    document.addEventListener("visibilitychange", onVis);
    const hb = setInterval(beat, 60000);
    const sq = setInterval(loadSquad, 30000);
    const chk = setInterval(() => {
      if (meRef.current?.status === "active" && Date.now() - last.current > AWAY_MS) beat();
    }, 30000);
    return () => { document.removeEventListener("visibilitychange", onVis); evs.forEach((e) => window.removeEventListener(e, mark)); clearInterval(hb); clearInterval(sq); clearInterval(chk); };
  }, [beat, loadSquad, persist]);

  const setStatus = useCallback(async (status, reason) => {
    if (status === "offline") await beat();
    const { data } = await api.post("/squad/status", { status, reason });
    setMe(data); loadSquad();
  }, [loadSquad, beat]);

  return (
    <Ctx.Provider value={{ me, squad, setStatus, loadSquad }}>
      {children}
    </Ctx.Provider>
  );
}
